import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type DragEvent,
    type KeyboardEvent,
    type MouseEvent,
} from 'react'
import { Controller } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import type {
    BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
    SurfacePropertyType,
} from '@/common/api/client/types.gen'
import { resolveRelativeAssetUrl } from '@/common/lib/assetsUrl'
import { cn } from '@/common/lib/cn/cn'
import type { ProductMediaImageGroupsState } from '../../model'
import { useProductFlowForm } from '../../model/productFlow'
import { useProductTextsGeneration } from '../../model/textsGeneration'
import { resolveDefaultMarketplaceTexts } from '../../lib/marketplaceTexts'
import { Card } from '@/common/ui/Card'
import { ChoiceGroup } from '@/common/ui/ChoiceGroup'
import { Button } from '@/common/ui/Form/Button'
import { CollapsibleCard } from '@/common/ui/CollapsibleCard'
import { Fieldset } from '@/common/ui/Form/Fieldset'
import { Input } from '@/common/ui/Form/Input'
import { Select, SelectOption } from '@/common/ui/Form/Select'
import { SelectImageFromGallery } from '@/common/ui/Form/SelectImageFromGallery'
import { Textarea } from '@/common/ui/Form/Textarea'
import { TokenAmount } from '@/common/ui/TokenAmount'
import { StickyBottomBar } from '@/common/ui/StickyBottomBar/StickyBottomBar'
import { CardSelectMenu, type CardSelectMenuItem } from '@/common/ui/Form/CardSelectMenu'
import { SearchableSelect, type SearchableSelectOption } from '@/common/ui/SearchableSelect'
import { fetchGenerateImageCategories, mapCategoriesToOptions } from '@/app/entities/product/api/productCategoryOptions'
import { fetchGenerateImageVariants } from '@/app/entities/product/api/productImageVariants'
import type { MarketplaceTextsSelection } from '../Texts/TextsGenerationsList'
import { MarketplaceDataPickerModal } from './MarketplaceDataPickerModal'
import { MARKETPLACE_DATA_PICKER_MODAL_ID } from './marketplaceDataPickerModalState'

type MediaFormProps = {
    media: ProductMediaImageGroupsState
}

const getProductImageUrl = (image: { [key: string]: unknown }): string | null => {
    if (!('big' in image)) {
        return null
    }
    const big = image.big
    return typeof big === 'string' ? big : null
}

const SURFACE_PROPERTY_OPTIONS: { value: SurfacePropertyType; label: string }[] = [
    { value: 'MATT', label: 'Матовая' },
    { value: 'GLOSSY', label: 'Глянцевая' },
]

const FONT_OPTIONS = [
    'Roboto',
    'Arial',
    'Verdana',
    'Tahoma',
    'Trebuchet MS',
    'Courier New',
    'Times New Roman',
    'Calibri',
] as const

const COLOR_OPTIONS = [
    { value: '#000000', label: 'Черный' },
    { value: '#FFFFFF', label: 'Белый' },
    { value: '#F5F5F5', label: 'Светло-серый' },
    { value: '#808080', label: 'Серый' },
    { value: '#FF0000', label: 'Красный' },
    { value: '#FFA500', label: 'Оранжевый' },
    { value: '#FFFF00', label: 'Желтый' },
    { value: '#008000', label: 'Зеленый' },
    { value: '#0000FF', label: 'Синий' },
    { value: '#800080', label: 'Фиолетовый' },
] as const

type StyleMode = 'ai' | 'manual' | 'sample'
type BackgroundMode = 'ai' | 'color' | 'image'

type FileDropzoneInputProps = {
    value: File | null
    onChange: (file: File | null) => void
    onBlur: () => void
    name: string
    inputRef: (instance: HTMLInputElement | null) => void
    accept: string
    disabled: boolean
    ariaLabel: string
}

const FileDropzoneInput = ({
    value,
    onChange,
    onBlur,
    name,
    inputRef,
    accept,
    disabled,
    ariaLabel,
}: FileDropzoneInputProps) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [isFileDragOver, setIsFileDragOver] = useState(false)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!(value instanceof File) || !value.type.startsWith('image/')) {
            setPreviewUrl(null)
            return
        }
        const url = URL.createObjectURL(value)
        setPreviewUrl(url)
        return () => {
            URL.revokeObjectURL(url)
        }
    }, [value])

    const assignInputRef = (instance: HTMLInputElement | null) => {
        fileInputRef.current = instance
        inputRef(instance)
    }

    const handleChooseClick = () => {
        if (disabled) {
            return
        }
        fileInputRef.current?.click()
    }

    const handleFileFromInput = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null
        onChange(file)
    }

    const handleRemoveFile = () => {
        if (disabled) {
            return
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
        onChange(null)
    }

    const handleFileDragEnter = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (disabled) {
            return
        }
        setIsFileDragOver(true)
    }

    const handleFileDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
            return
        }
        setIsFileDragOver(false)
    }

    const handleFileDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (disabled) {
            return
        }
        event.dataTransfer.dropEffect = 'copy'
    }

    const handleFileDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsFileDragOver(false)
        if (disabled) {
            return
        }
        const file = event.dataTransfer.files?.[0] ?? null
        onChange(file)
    }

    return (
        <>
            <input
                ref={assignInputRef}
                size={1}
                type='file'
                className='hidden'
                disabled={disabled}
                accept={accept}
                aria-label={ariaLabel}
                name={name}
                onBlur={onBlur}
                onChange={handleFileFromInput}
            />
            <div
                role='region'
                aria-label='Зона загрузки изображения. Можно перетащить файл сюда.'
                onDragEnter={handleFileDragEnter}
                onDragLeave={handleFileDragLeave}
                onDragOverCapture={handleFileDragOver}
                onDropCapture={handleFileDrop}
                className={cn(
                    'rounded-[10px] border border-dashed border-[var(--pixorion-gray-2)] bg-[rgba(255,255,255,0.03)] px-4 py-5',
                    'flex flex-col items-center gap-3 text-center transition-colors',
                    isFileDragOver && !disabled && 'border-[var(--pixorion-brand)] bg-[rgba(255,255,255,0.07)]',
                    disabled && 'cursor-not-allowed opacity-60'
                )}
            >
                {value ? (
                    <>
                        {previewUrl ? (
                            <img
                                src={previewUrl}
                                alt={`Предпросмотр файла ${value.name}`}
                                className='max-h-40 max-w-full rounded-[8px] object-contain'
                            />
                        ) : null}
                        <span className='text-sm text-[var(--pixorion-gray-2)] break-all' title={value.name}>
                            {value.name}
                        </span>
                        <div className='flex gap-2'>
                            <Button
                                type='button'
                                size='md'
                                emphasis='accent'
                                variant='outline'
                                onClick={handleChooseClick}
                                disabled={disabled}
                            >
                                Заменить файл
                            </Button>
                            <Button
                                type='button'
                                size='md'
                                variant='outline'
                                onClick={handleRemoveFile}
                                disabled={disabled}
                                aria-label='Удалить загруженный файл'
                            >
                                Удалить
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <span className='text-sm text-[var(--pixorion-gray-2)]'>Загрузите или перетащите фото</span>
                        <Button
                            type='button'
                            size='md'
                            emphasis='accent'
                            variant='outline'
                            onClick={handleChooseClick}
                            disabled={disabled}
                        >
                            Выбрать файл
                        </Button>
                    </>
                )}
            </div>
        </>
    )
}

const getFontPreviewSvgDataUrl = (fontName: string) => {
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="96" height="24" viewBox="0 0 96 24" fill="none">
            <rect width="96" height="24" rx="6" fill="#1F1F23"/>
            <text
                x="48"
                y="16"
                fill="#FFFFFF"
                font-family="${fontName}"
                font-size="12"
                text-anchor="middle"
            >
                Пример
            </text>
        </svg>
    `.trim()

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

const getDefaultGenerateImagesFormValues =
    (): BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost => ({
        category_id: '',
        image_variants: [],
        image_quantites: [],
        images: [],
        font: 'Roboto',
        product_name: null,
        product_description: null,
        font_color: null,
        bg_color: null,
        bg_property: null,
        bg_image: null,
        font_image_image: null,
        reference: null,
    })

const normalizeNullableText = (value: string | null | undefined) => {
    const trimmedValue = value?.trim()
    return trimmedValue ? trimmedValue : null
}

const normalizeSubmitBody = (
    data: BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
    defaultProductData?: Pick<
        BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
        'product_name' | 'product_description'
    >,
    options?: { isReferencePromptType?: boolean }
): BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost => {
    const images = (data.images ?? []).filter((item): item is File => item instanceof File)
    const variants = data.image_variants ?? []
    const quantities = data.image_quantites ?? []
    const productName = normalizeNullableText(data.product_name)
    const productDescription = normalizeNullableText(data.product_description)
    const defaultProductName = normalizeNullableText(defaultProductData?.product_name)
    const defaultProductDescription = normalizeNullableText(defaultProductData?.product_description)
    const alignedQuantities = variants.map((_, index) => {
        const raw = quantities[index]
        const n = typeof raw === 'number' && Number.isFinite(raw) ? Math.floor(raw) : 1
        return Math.max(1, n)
    })
    const isReferencePromptType = Boolean(options?.isReferencePromptType)
    const reference = data.reference instanceof File ? data.reference : null

    const normalized: BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost = {
        ...data,
        images,
        category_id: data.category_id?.trim() ?? '',
        image_variants: variants,
        image_quantites: alignedQuantities.length === variants.length ? alignedQuantities : variants.map(() => 1),
        product_name: productName,
        product_description: productDescription,
        font: isReferencePromptType ? null : data.font?.trim() ? data.font.trim() : null,
        font_color: isReferencePromptType ? null : data.font_color?.trim() ? data.font_color.trim() : null,
        bg_color: isReferencePromptType ? null : data.bg_color?.trim() ? data.bg_color.trim() : null,
        bg_property:
            isReferencePromptType || (data.bg_property !== 'GLOSSY' && data.bg_property !== 'MATT')
                ? null
                : data.bg_property,
        bg_image: isReferencePromptType ? null : data.bg_image instanceof File ? data.bg_image : null,
        font_image_image: isReferencePromptType
            ? null
            : data.font_image_image instanceof File
              ? data.font_image_image
              : null,
        reference: isReferencePromptType ? reference : null,
    }

    if (productName === defaultProductName) {
        delete normalized.product_name
    }
    if (productDescription === defaultProductDescription) {
        delete normalized.product_description
    }

    return normalized
}

const normalizeTokenCost = (cost: number | null | undefined) => {
    if (typeof cost !== 'number' || !Number.isFinite(cost)) {
        return 0
    }

    return Math.max(0, cost)
}

export const MediaForm = ({ media }: MediaFormProps) => {
    const {
        isProductLoading,
        submitGeneration,
        generationMutation,
        detailFormPipelineLoading,
        orgId,
        productId,
        product,
    } = media

    const texts = useProductTextsGeneration()

    const galleryImages = useMemo(() => {
        if (!product?.images?.length) {
            return []
        }
        return product.images
            .map(imageItem => {
                const url = getProductImageUrl(imageItem)
                if (!url) {
                    return null
                }
                return resolveRelativeAssetUrl(url)
            })
            .filter((url): url is string => Boolean(url))
    }, [product?.images])

    const fontPreviewByName = useMemo(() => {
        return Object.fromEntries(
            FONT_OPTIONS.map(fontName => [fontName, getFontPreviewSvgDataUrl(fontName)])
        ) as Record<(typeof FONT_OPTIONS)[number], string>
    }, [])

    const productIdForDefaults = product?.id

    const resolvedMarketplaceTexts = useMemo(
        () =>
            resolveDefaultMarketplaceTexts({
                listItems: texts.generationListData?.items,
                detail: texts.detailQuery.data,
                product,
            }),
        [product, texts.detailQuery.data, texts.generationListData?.items]
    )

    const defaultValues = useMemo((): BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost => {
        const base = getDefaultGenerateImagesFormValues()
        if (!productIdForDefaults) {
            return base
        }
        return {
            ...base,
            product_name: resolvedMarketplaceTexts.title,
            product_description: resolvedMarketplaceTexts.description,
        }
    }, [productIdForDefaults, resolvedMarketplaceTexts.description, resolvedMarketplaceTexts.title])

    const { control, register, handleSubmit, reset, formState, watch, setValue, setError, clearErrors } =
        useProductFlowForm<BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost>({
            defaultValues,
            generationMutation,
        })

    const watchedImageVariants = watch('image_variants')
    const watchedImageQuantites = watch('image_quantites')
    const imageVariants = useMemo(() => watchedImageVariants ?? [], [watchedImageVariants])
    const imageQuantites = useMemo(() => watchedImageQuantites ?? [], [watchedImageQuantites])
    const categoryId = watch('category_id') ?? ''
    const autoSelectVariantsCategoryRef = useRef<string | null>(null)
    const [styleMode, setStyleMode] = useState<StyleMode>('ai')
    const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>('ai')

    const isManualStyle = styleMode === 'manual'
    const isSampleStyle = styleMode === 'sample'
    const isColorBackground = backgroundMode === 'color'
    const isImageBackground = backgroundMode === 'image'
    const [isProductPhotosOpen, setIsProductPhotosOpen] = useState(true)
    const [isMarketplaceDataOpen, setIsMarketplaceDataOpen] = useState(false)
    const [isImageTypesOpen, setIsImageTypesOpen] = useState(true)
    const [isReferenceOpen, setIsReferenceOpen] = useState(true)
    const [isBrandingOpen, setIsBrandingOpen] = useState(false)

    const hasProductPhotosErrors = Boolean(formState.errors.images)
    const hasMarketplaceDataErrors = Boolean(formState.errors.product_name || formState.errors.product_description)
    const hasImageTypesErrors = Boolean(
        formState.errors.category_id || formState.errors.image_variants || formState.errors.image_quantites
    )
    const hasReferenceErrors = Boolean(formState.errors.reference)
    const hasBrandingErrors = Boolean(
        formState.errors.font ||
            formState.errors.font_color ||
            formState.errors.font_image_image ||
            formState.errors.bg_color ||
            formState.errors.bg_image ||
            formState.errors.bg_property
    )

    useEffect(() => {
        if (hasProductPhotosErrors) {
            setIsProductPhotosOpen(true)
        }
    }, [hasProductPhotosErrors])

    useEffect(() => {
        if (hasMarketplaceDataErrors) {
            setIsMarketplaceDataOpen(true)
        }
    }, [hasMarketplaceDataErrors])

    useEffect(() => {
        if (hasImageTypesErrors) {
            setIsImageTypesOpen(true)
        }
    }, [hasImageTypesErrors])

    useEffect(() => {
        if (hasReferenceErrors) {
            setIsReferenceOpen(true)
        }
    }, [hasReferenceErrors])

    useEffect(() => {
        if (hasBrandingErrors) {
            setIsBrandingOpen(true)
        }
    }, [hasBrandingErrors])

    useEffect(() => {
        if (isProductLoading || !texts.generationListResolved) {
            return
        }
        reset(defaultValues, { keepDirtyValues: true })
    }, [defaultValues, isProductLoading, reset, texts.generationListResolved])

    useEffect(() => {
        if (isManualStyle) {
            setValue('font_image_image', null, { shouldDirty: true })
            return
        }

        if (isSampleStyle) {
            setValue('font_color', null, { shouldDirty: true })
            setValue('font', null, { shouldDirty: true })
            return
        }

        setValue('font_color', null, { shouldDirty: true })
        setValue('font', null, { shouldDirty: true })
        setValue('font_image_image', null, { shouldDirty: true })
    }, [isManualStyle, isSampleStyle, setValue])

    useEffect(() => {
        if (isColorBackground) {
            setValue('bg_image', null, { shouldDirty: true })
            return
        }

        if (isImageBackground) {
            setValue('bg_color', null, { shouldDirty: true })
            return
        }

        setValue('bg_color', null, { shouldDirty: true })
        setValue('bg_image', null, { shouldDirty: true })
    }, [isColorBackground, isImageBackground, setValue])

    /** До конца long poll по detail (терминальный status группы), не только HTTP мутации */
    const isSubmitting = detailFormPipelineLoading
    const canSubmit = Boolean(orgId && productId && product)
    const isFieldsDisabled = detailFormPipelineLoading

    const handleMarketplaceDataSelect = useCallback(
        (data: MarketplaceTextsSelection) => {
            setValue('product_name', data.title, { shouldDirty: true })
            setValue('product_description', data.description, { shouldDirty: true })
        },
        [setValue]
    )

    const handleOpenMarketplaceDataPicker = useCallback(() => {
        if (isFieldsDisabled) {
            return
        }

        const checkbox = document.getElementById(MARKETPLACE_DATA_PICKER_MODAL_ID) as HTMLInputElement | null
        if (checkbox) {
            checkbox.checked = true
        }
    }, [isFieldsDisabled])

    const handleOpenMarketplaceDataPickerClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            handleOpenMarketplaceDataPicker()
        },
        [handleOpenMarketplaceDataPicker]
    )

    const handleOpenMarketplaceDataPickerKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>) => {
        event.stopPropagation()
    }, [])

    const categoriesQuery = useQuery({
        queryKey: ['generate-image-categories', orgId],
        queryFn: () => fetchGenerateImageCategories(orgId!),
        enabled: Boolean(orgId),
        staleTime: 60_000,
    })

    const categoryOptions = useMemo<SearchableSelectOption[]>(
        () => mapCategoriesToOptions(categoriesQuery.data ?? []),
        [categoriesQuery.data]
    )

    const selectedCategory = useMemo(
        () => (categoriesQuery.data ?? []).find(category => category.category_id === categoryId) ?? null,
        [categoriesQuery.data, categoryId]
    )
    const isReferencePromptType = selectedCategory?.prompt_type === 'REFERENCE'

    const selectedCategoryLabel = useMemo(
        () => categoryOptions.find(o => o.value === categoryId)?.label ?? null,
        [categoryOptions, categoryId]
    )

    useEffect(() => {
        if (isReferencePromptType) {
            setValue('font', null, { shouldDirty: true })
            setValue('font_color', null, { shouldDirty: true })
            setValue('font_image_image', null, { shouldDirty: true })
            setValue('bg_color', null, { shouldDirty: true })
            setValue('bg_image', null, { shouldDirty: true })
            setValue('bg_property', null, { shouldDirty: true })
            setStyleMode('ai')
            setBackgroundMode('ai')
            return
        }

        setValue('reference', null, { shouldDirty: true })
        clearErrors('reference')
    }, [clearErrors, isReferencePromptType, setValue])

    const loadCategoryOptions = useCallback(
        async (q: string) => {
            const query = q.trim().toLowerCase()
            if (!query) {
                return categoryOptions
            }
            return categoryOptions.filter(o => o.label.toLowerCase().includes(query))
        },
        [categoryOptions]
    )

    useEffect(() => {
        if (!categoriesQuery.isSuccess || categoryId) {
            return
        }
        const defaultCategory = (categoriesQuery.data ?? []).find(c => c.is_default && c.category_id)
        if (defaultCategory?.category_id) {
            setValue('category_id', defaultCategory.category_id, { shouldValidate: true })
        }
    }, [categoriesQuery.isSuccess, categoriesQuery.data, categoryId, setValue])

    useEffect(() => {
        autoSelectVariantsCategoryRef.current = categoryId || null
    }, [categoryId])

    const variantsQuery = useQuery({
        queryKey: ['generate-image-variants', orgId, categoryId],
        queryFn: () => fetchGenerateImageVariants(orgId!, categoryId),
        enabled: Boolean(orgId && categoryId),
        staleTime: 60_000,
    })

    const variants = useMemo(() => variantsQuery.data ?? [], [variantsQuery.data])
    const generationTokenAmount = useMemo(() => {
        const costByVariantId = new Map(variants.map(variant => [variant.id, normalizeTokenCost(variant.cost_tokens)]))

        return imageVariants.reduce((total, variantId, index) => {
            const quantity = imageQuantites[index]
            const normalizedQuantity =
                typeof quantity === 'number' && Number.isFinite(quantity) ? Math.floor(quantity) : 1

            return total + Math.max(1, normalizedQuantity) * (costByVariantId.get(variantId) ?? 0)
        }, 0)
    }, [imageQuantites, imageVariants, variants])

    useEffect(() => {
        if (!variantsQuery.isSuccess) {
            return
        }
        if (autoSelectVariantsCategoryRef.current === categoryId) {
            const nextIds = variants.map(variant => variant.id)
            setValue('image_variants', nextIds, { shouldDirty: true, shouldValidate: true })
            setValue(
                'image_quantites',
                nextIds.map(() => 1),
                { shouldDirty: true }
            )
            autoSelectVariantsCategoryRef.current = null
            return
        }
        const validIds = new Set(variants.map(v => v.id))
        const filtered: { ids: string[]; qty: number[] } = { ids: [], qty: [] }
        imageVariants.forEach((id, index) => {
            if (validIds.has(id)) {
                filtered.ids.push(id)
                filtered.qty.push(imageQuantites[index] ?? 1)
            }
        })
        if (filtered.ids.length !== imageVariants.length) {
            setValue('image_variants', filtered.ids, { shouldDirty: true })
            setValue('image_quantites', filtered.qty, { shouldDirty: true })
        }
    }, [variantsQuery.isSuccess, variants, categoryId, imageVariants, imageQuantites, setValue])

    const handleCategoryChange = (next: string | null) => {
        const value = next ?? ''
        setValue('category_id', value, { shouldDirty: true, shouldValidate: true })
        setValue('image_variants', [], { shouldDirty: true })
        setValue('image_quantites', [], { shouldDirty: true })
        clearErrors(['category_id', 'image_variants'])
    }

    const variantItems = useMemo<CardSelectMenuItem[]>(
        () =>
            variants.map(variant => ({
                id: variant.id,
                title: variant.name,
                description: variant.description ?? '',
                image: variant.image_url ? resolveRelativeAssetUrl(variant.image_url) : null,
            })),
        [variants]
    )

    const handleValidSubmit = (values: BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost) => {
        clearErrors()
        const normalized = normalizeSubmitBody(
            values,
            {
                product_name: resolvedMarketplaceTexts.title,
                product_description: resolvedMarketplaceTexts.description,
            },
            { isReferencePromptType }
        )
        if (!normalized.category_id) {
            setError('category_id', { type: 'manual', message: 'Выберите категорию' })
            return
        }
        if (normalized.image_variants.length === 0) {
            setError('image_variants', { type: 'manual', message: 'Выберите хотя бы один вариант' })
            return
        }
        if (normalized.images.length === 0) {
            setError('images', { type: 'manual', message: 'Добавьте изображение товара' })
            return
        }
        submitGeneration(normalized)
    }

    return (
        <>
            {isProductLoading && (
                <div className='flex justify-center items-center h-32' role='status' aria-live='polite'>
                    Загрузка...
                </div>
            )}
            {!isProductLoading && (
                <form onSubmit={handleSubmit(handleValidSubmit)}>
                    <Card type='result-last' cardBodyClassName='p-[20px]!'>
                        {/*<div className='flex flex-col gap-2'>
                            <Button
                                type='button'
                                variant='outline'
                                size='md'
                                disabled={isFieldsDisabled}
                                onClick={handleToggleLsPrompt}
                                onKeyDown={handleToggleLsPromptKeyDown}
                                aria-expanded={isLsPromptOpen}
                                aria-controls='media-ls-prompt-panel'
                                aria-label={isLsPromptOpen ? 'Скрыть блок LS prompt' : 'Показать блок LS prompt'}
                            >
                                {isLsPromptOpen ? 'Скрыть LS prompt' : 'Показать LS prompt'}
                            </Button>
                            {isLsPromptOpen && <MediaLsPromptPanel />}
                        </div>*/}
                        <h3>Настройки генерации фотографий</h3>

                        <div className='flex flex-col gap-3'>
                            <CollapsibleCard
                                title='Фото товара'
                                isOpen={isProductPhotosOpen}
                                onToggle={setIsProductPhotosOpen}
                            >
                                <Fieldset
                                    label='Ключевое изображение'
                                    required
                                    errorText={
                                        formState.errors.images?.message ?? formState.errors.images?.root?.message
                                    }
                                >
                                    <SelectImageFromGallery
                                        control={control}
                                        name='images'
                                        disabled={isFieldsDisabled}
                                        galleryImages={galleryImages}
                                        title=''
                                        emptyText='Фотографии в галерее отсутствуют'
                                        multiple
                                        maxFiles={5}
                                        autoSelectFirst
                                    />
                                </Fieldset>
                            </CollapsibleCard>

                            <CollapsibleCard
                                title='Данные маркетплейса'
                                isOpen={isMarketplaceDataOpen}
                                onToggle={setIsMarketplaceDataOpen}
                                headerAction={
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='sm'
                                        disabled={isFieldsDisabled}
                                        aria-label='Выбрать данные маркетплейса из списка'
                                        onClick={handleOpenMarketplaceDataPickerClick}
                                        onKeyDown={handleOpenMarketplaceDataPickerKeyDown}
                                    >
                                        Выбрать из списка
                                    </Button>
                                }
                            >
                                <Fieldset label='Название' errorText={formState.errors.product_name?.message}>
                                    <Input
                                        size='md'
                                        className='w-full'
                                        placeholder='Название товара'
                                        disabled={isFieldsDisabled}
                                        type='text'
                                        {...register('product_name')}
                                    />
                                </Fieldset>
                                <Fieldset label='Описание' errorText={formState.errors.product_description?.message}>
                                    <Textarea
                                        size='md'
                                        disabled={isFieldsDisabled}
                                        className='w-full min-h-[120px]'
                                        placeholder='Заполните описание товара, которое будет использовано для создания изображений'
                                        {...register('product_description')}
                                    />
                                </Fieldset>
                            </CollapsibleCard>

                            <CollapsibleCard
                                title='Типы изображений'
                                isOpen={isImageTypesOpen}
                                onToggle={setIsImageTypesOpen}
                            >
                                <div className='flex gap-[8px] items-center w-full mb-1'>
                                    <h3 className='basis-0 font-medium grow leading-[22px] text-lg text-white'>
                                        Выберите тип изображения
                                    </h3>
                                </div>
                                <Fieldset label='Категория' required errorText={formState.errors.category_id?.message}>
                                    <Controller
                                        name='category_id'
                                        control={control}
                                        render={({ field }) => (
                                            <SearchableSelect
                                                value={field.value ? field.value : null}
                                                onValueChange={handleCategoryChange}
                                                onBlur={field.onBlur}
                                                loadOptions={loadCategoryOptions}
                                                selectedLabel={selectedCategoryLabel}
                                                placeholder='Категория товара'
                                                searchPlaceholder='Поиск категории…'
                                                disabled={isFieldsDisabled || categoriesQuery.isLoading}
                                                size='md'
                                                minQueryLength={0}
                                                className='w-full'
                                            />
                                        )}
                                    />
                                </Fieldset>
                                <p className='text-sm text-[var(--pixorion-gray-2)] mb-3'>
                                    Выберите категорию выше, затем отметьте нужные варианты.
                                </p>
                                {categoryId && variantsQuery.isLoading && (
                                    <p className='text-sm text-[var(--pixorion-gray-2)]' aria-live='polite'>
                                        Загрузка вариантов…
                                    </p>
                                )}
                                {categoryId && variantsQuery.isError && (
                                    <p className='text-sm text-red-400' role='alert'>
                                        {variantsQuery.error instanceof Error
                                            ? variantsQuery.error.message
                                            : 'Не удалось загрузить варианты'}
                                    </p>
                                )}
                                {categoryId &&
                                    variantsQuery.isSuccess &&
                                    variants.length === 0 &&
                                    !variantsQuery.isFetching && (
                                        <p className='text-sm text-[var(--pixorion-gray-2)]'>
                                            Для выбранной категории нет доступных вариантов.
                                        </p>
                                    )}
                                {categoryId && variants.length > 0 && (
                                    <CardSelectMenu
                                        mode='multi'
                                        control={control}
                                        name='image_variants'
                                        quantityName='image_quantites'
                                        items={variantItems}
                                        disabled={isFieldsDisabled}
                                    />
                                )}
                                {formState.errors.image_variants && (
                                    <p className='text-sm text-red-400 mt-2' role='alert'>
                                        {formState.errors.image_variants.message as string}
                                    </p>
                                )}
                            </CollapsibleCard>
                            {isReferencePromptType && (
                                <CollapsibleCard
                                    title='Референс'
                                    isOpen={isReferenceOpen}
                                    onToggle={setIsReferenceOpen}
                                >
                                    <Fieldset
                                        label='Референсное изображение'
                                        errorText={formState.errors.reference?.message}
                                    >
                                        <Controller
                                            control={control}
                                            name='reference'
                                            render={({ field }) => (
                                                <FileDropzoneInput
                                                    value={field.value instanceof File ? field.value : null}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    inputRef={field.ref}
                                                    disabled={isFieldsDisabled}
                                                    accept='image/png,image/jpeg,image/webp'
                                                    ariaLabel='Загрузить референсное изображение'
                                                />
                                            )}
                                        />
                                    </Fieldset>
                                </CollapsibleCard>
                            )}
                            {!isReferencePromptType && (
                                <CollapsibleCard
                                    title='Стиль и брендинг'
                                    isOpen={isBrandingOpen}
                                    onToggle={setIsBrandingOpen}
                                >
                                    <Fieldset className='min-w-0' label='Стиль оформления'>
                                        <div className='min-w-0 w-full max-w-full overflow-hidden'>
                                            <ChoiceGroup
                                                className='w-full max-w-full'
                                                items={['Нейросеть', 'Ручной выбор', 'Загрузка примера']}
                                                activeIndex={styleMode === 'ai' ? 0 : styleMode === 'manual' ? 1 : 2}
                                                onChange={index => {
                                                    const nextStyleMode: StyleMode =
                                                        index === 1 ? 'manual' : index === 2 ? 'sample' : 'ai'
                                                    setStyleMode(nextStyleMode)
                                                }}
                                            />
                                        </div>
                                    </Fieldset>
                                    {isManualStyle && (
                                        <>
                                            <Fieldset
                                                label='Цвет шрифта'
                                                errorText={formState.errors.font_color?.message}
                                            >
                                                <Select
                                                    size='md'
                                                    className='w-full'
                                                    placeholder='Выберите цвет шрифта'
                                                    disabled={isFieldsDisabled}
                                                    {...register('font_color')}
                                                >
                                                    <SelectOption value=''>Не выбрано</SelectOption>
                                                    {COLOR_OPTIONS.map(({ value, label }) => (
                                                        <SelectOption
                                                            key={value}
                                                            value={value}
                                                            className='flex items-center gap-2'
                                                            leftSlot={
                                                                <div
                                                                    className='flex items-center gap-2 rounded-[4px] min-w-[18px] h-[18px]'
                                                                    style={{ backgroundColor: value }}
                                                                    aria-hidden
                                                                ></div>
                                                            }
                                                        >
                                                            {label}
                                                        </SelectOption>
                                                    ))}
                                                </Select>
                                            </Fieldset>
                                            <Fieldset label='Шрифт' errorText={formState.errors.font?.message}>
                                                <Select
                                                    size='md'
                                                    className='w-full'
                                                    placeholder='Шрифт'
                                                    disabled={isFieldsDisabled}
                                                    {...register('font')}
                                                >
                                                    {FONT_OPTIONS.map(fontName => (
                                                        <SelectOption
                                                            key={fontName}
                                                            value={fontName}
                                                            rightSlot={
                                                                <img
                                                                    src={fontPreviewByName[fontName]}
                                                                    alt=''
                                                                    aria-hidden
                                                                    className='h-6 w-24 rounded-[6px] object-contain'
                                                                />
                                                            }
                                                        >
                                                            {fontName}
                                                        </SelectOption>
                                                    ))}
                                                </Select>
                                            </Fieldset>
                                        </>
                                    )}
                                    {isSampleStyle && (
                                        <Fieldset
                                            label='Образец шрифта (файл)'
                                            errorText={formState.errors.font_image_image?.message}
                                        >
                                            <Controller
                                                control={control}
                                                name='font_image_image'
                                                render={({ field }) => (
                                                    <FileDropzoneInput
                                                        value={field.value instanceof File ? field.value : null}
                                                        onChange={field.onChange}
                                                        onBlur={field.onBlur}
                                                        name={field.name}
                                                        inputRef={field.ref}
                                                        disabled={isFieldsDisabled}
                                                        accept='image/png,image/jpeg,image/webp,.ttf,.otf,.woff,.woff2'
                                                        ariaLabel='Загрузить образец шрифта'
                                                    />
                                                )}
                                            />
                                        </Fieldset>
                                    )}
                                    <Fieldset className='min-w-0' label='Тип фона'>
                                        <div className='min-w-0 w-full max-w-full overflow-hidden'>
                                            <ChoiceGroup
                                                className='w-full max-w-full'
                                                items={['Нейросеть', 'Цвет', 'Загрузка картинки']}
                                                activeIndex={
                                                    backgroundMode === 'ai' ? 0 : backgroundMode === 'color' ? 1 : 2
                                                }
                                                onChange={index => {
                                                    const nextBackgroundMode: BackgroundMode =
                                                        index === 1 ? 'color' : index === 2 ? 'image' : 'ai'
                                                    setBackgroundMode(nextBackgroundMode)
                                                }}
                                            />
                                        </div>
                                    </Fieldset>
                                    {isColorBackground && (
                                        <Fieldset label='Цвет фона' errorText={formState.errors.bg_color?.message}>
                                            <Select
                                                size='md'
                                                className='w-full'
                                                placeholder='Выберите цвет фона'
                                                disabled={isFieldsDisabled}
                                                {...register('bg_color')}
                                            >
                                                <SelectOption value=''>Не выбрано</SelectOption>
                                                {COLOR_OPTIONS.map(({ value, label }) => (
                                                    <SelectOption
                                                        key={value}
                                                        value={value}
                                                        className='flex items-center gap-2'
                                                        leftSlot={
                                                            <div
                                                                className='flex items-center gap-2 rounded-[4px] min-w-[18px] h-[18px]'
                                                                style={{ backgroundColor: value }}
                                                                aria-hidden
                                                            ></div>
                                                        }
                                                    >
                                                        {label}
                                                    </SelectOption>
                                                ))}
                                            </Select>
                                        </Fieldset>
                                    )}

                                    {isImageBackground && (
                                        <Fieldset label='Фон (файл)' errorText={formState.errors.bg_image?.message}>
                                            <Controller
                                                control={control}
                                                name='bg_image'
                                                render={({ field }) => (
                                                    <FileDropzoneInput
                                                        value={field.value instanceof File ? field.value : null}
                                                        onChange={field.onChange}
                                                        onBlur={field.onBlur}
                                                        name={field.name}
                                                        inputRef={field.ref}
                                                        disabled={isFieldsDisabled}
                                                        accept='image/png,image/jpeg,image/webp'
                                                        ariaLabel='Загрузить фоновое изображение'
                                                    />
                                                )}
                                            />
                                        </Fieldset>
                                    )}
                                    <Fieldset
                                        label='Поверхность фона'
                                        errorText={formState.errors.bg_property?.message}
                                    >
                                        <Select
                                            size='md'
                                            className='w-full'
                                            placeholder='Не выбрано'
                                            disabled={isFieldsDisabled}
                                            {...register('bg_property')}
                                        >
                                            <SelectOption value=''>Не выбрано</SelectOption>
                                            {SURFACE_PROPERTY_OPTIONS.map(({ value, label }) => (
                                                <SelectOption key={value} value={value}>
                                                    {label}
                                                </SelectOption>
                                            ))}
                                        </Select>
                                    </Fieldset>
                                </CollapsibleCard>
                            )}
                        </div>

                        {formState.errors.root?.message && (
                            <p className='text-sm text-red-400 mt-3' role='alert'>
                                {formState.errors.root.message}
                            </p>
                        )}
                    </Card>
                    <StickyBottomBar contentClassName='pr-[100px] xl:pr-[45px]'>
                        <span className='md:block hidden text-sm text-[var(--pixorion-gray-2)]'>2-3 минуты</span>
                        <Button
                            type='submit'
                            variant={isSubmitting ? 'outline' : 'filled'}
                            disabled={isSubmitting || !canSubmit}
                            loading={isSubmitting}
                            size='lg'
                            aria-label='Запустить генерацию изображений по товару'
                        >
                            {isSubmitting ? 'Генерация…' : 'Запустить генерацию'}
                            {!isSubmitting && generationTokenAmount > 0 && (
                                <TokenAmount value={generationTokenAmount} />
                            )}
                            {!isSubmitting && generationTokenAmount == 0 && <TokenAmount value={25} labelBefore='от' />}
                        </Button>
                    </StickyBottomBar>
                </form>
            )}
            <MarketplaceDataPickerModal textsState={texts} onSelect={handleMarketplaceDataSelect} />
        </>
    )
}
